if (!customElements.get('wlsn-header')) {
  class WLSNHeader extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'wlsn-header--hidden',
        sticky: 'wlsn-header--sticky',
        stickyScrollUp: 'wlsn-header--sticky-scroll-up',
        section: 'wlsn-header-section',
        headerMenuWrapper: 'js-header-menu-wrapper',
        lockScroll: 'wlsn-lock-scroll',
        mobileMenuToggle: 'js-mobile-menu-toggle',
        megaMenuOverlay: 'js-wlsn-megamenu-dropdown-overlay',
        wlsnAccordionElement: 'wlsn-accordion',
      };

      this.behavior = this.dataset.headerBehavior || '';
      this.lastScroll = window.scrollY;
      this.isMobileMenuOpen = false;
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        headerSections: document.querySelectorAll(`.${this.classes.section}`),
        headerMenuWrapper: this.querySelector(`.${this.classes.headerMenuWrapper}`),
        mobileMenuToggles: this.querySelectorAll(`.${this.classes.mobileMenuToggle}`),
        megaMenuOverlay: this.querySelector(`.${this.classes.megaMenuOverlay}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const { megaMenuOverlay, mobileMenuToggles, headerSections } = this.elements;

      if (megaMenuOverlay) {
        megaMenuOverlay.addEventListener('click', this._onMegaMenuOverlayClick.bind(this));
      }

      if (window.innerWidth < 1023) {
        mobileMenuToggles.forEach((toggle) => {
          toggle.addEventListener('click', this._mobileMenuToggle.bind(this));
        });
      }

      if (headerSections.length > 0) {
        headerSections.forEach((section) => {
          section.classList.remove(this.classes.sticky, this.classes.stickyScrollUp);
        });
        this._handleHeaderBehavior();
      }
      this._setHeaderHeight();

      // ESC key handle for all modals
      document.addEventListener('keydown', this._onEscKey.bind(this));
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      const { megaMenuOverlay, mobileMenuToggles, headerSections } = this.elements;

      if (megaMenuOverlay) {
        megaMenuOverlay.removeEventListener('click', this._onMegaMenuOverlayClick.bind(this));
      }

      if (window.innerWidth < 1023) {
        mobileMenuToggles.forEach((toggle) => {
          toggle.removeEventListener('click', this._mobileMenuToggle.bind(this));
        });
      }

      if (headerSections.length > 0) {
        headerSections.forEach((section) => {
          section.classList.remove(this.classes.sticky, this.classes.stickyScrollUp);
        });
      }

      if (this.behavior === 'sticky_scroll_up') {
        window.removeEventListener('scroll', this._handleScroll);
      }
      document.removeEventListener('keydown', this._handleEscKey);
    }

    /**
     * Reset accordion state by closing all accordions
     * @returns {void}
     */
    _resetAccordionState() {
      const { headerMenuWrapper } = this.elements;
      if (headerMenuWrapper) {
        headerMenuWrapper.querySelectorAll(`${this.classes.wlsnAccordionElement}`).forEach((acc) => {
          acc.dispatchEvent(new CustomEvent('wlsn:accordion:close-all', { bubbles: false }));
        });
      }
    }

    /**
     * Handle mega menu overlay click event
     * @returns {void}
     */
    _onMegaMenuOverlayClick() {
      const { headerMenuWrapper } = this.elements;
      if (headerMenuWrapper?.classList.contains(this.classes.active)) {
        this._resetMobileMenuState();
      }
    }

    /**
     * Toggle mobile menu open/closed
     * @returns {void}
     */
    _mobileMenuToggle() {
      const { headerMenuWrapper, megaMenuOverlay } = this.elements;
      if (headerMenuWrapper?.classList.contains(this.classes.active)) {
        this._resetMobileMenuState();
      } else {
        headerMenuWrapper?.classList.add(this.classes.active);
        megaMenuOverlay?.classList.add(this.classes.active);
        this._lockScroll();
        this._updateOverlayState();
        this.isMobileMenuOpen = true;
      }
    }

    /**
     * Update overlay state based on menu state
     * @returns {void}
     */
    _updateOverlayState() {
      const { megaMenuOverlay, headerMenuWrapper } = this.elements;
      if (megaMenuOverlay) {
        megaMenuOverlay.classList.toggle(
          this.classes.active,
          headerMenuWrapper.classList.contains(this.classes.active)
        );
      }
    }

    /**
     * Reset mobile menu to closed state
     * @returns {void}
     */
    _resetMobileMenuState() {
      const { headerMenuWrapper, megaMenuOverlay } = this.elements;
      headerMenuWrapper?.classList.remove(this.classes.active);
      megaMenuOverlay?.classList.remove(this.classes.active);
      this._unlockScroll();
      this._resetAccordionState();
      this._updateOverlayState();
      this.isMobileMenuOpen = false;
    }

    /**
     * Handle ESC key press
     * @param {KeyboardEvent} e - The keyboard event
     * @returns {void}
     */
    _onEscKey(e) {
      if (e.key === 'Escape') {
        if (this.isMobileMenuOpen) {
          this._resetMobileMenuState();
        }
      }
    }

    /**
     * Handle header behavior based on settings
     * @returns {void}
     */
    _handleHeaderBehavior() {
      const { headerSections } = this.elements;
      if (this.behavior === 'always_sticky') {
        headerSections.forEach((section) => {
          section.classList.add(this.classes.sticky);
        });
      } else if (this.behavior === 'sticky_scroll_up') {
        headerSections.forEach((section) => {
          section.classList.add(this.classes.stickyScrollUp);
        });
        window.addEventListener('scroll', this._handleScroll.bind(this));
      }
    }

    /**
     * Handle scroll event for sticky header
     * @returns {void}
     */
    _handleScroll() {
      const { headerSections } = this.elements;
      const currentScroll = window.scrollY;
      if (headerSections.length === 0) return;
      headerSections.forEach((section) => {
        section.classList.toggle(
          this.classes.hidden,
          currentScroll > this.lastScroll && currentScroll > 20
        );
      });
      this.lastScroll = currentScroll;
    }

    /**
     * Set header height CSS variable
     * @returns {void}
     */
    _setHeaderHeight() {
      const headerHeight = this.offsetHeight;
      document.documentElement.style.setProperty('--wlsn-header-height', `${headerHeight}px`);
    }

    /**
     * Lock body scroll
     * @returns {void}
     */
    _lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    /**
     * Unlock body scroll
     * @returns {void}
     */
    _unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }
  }
  customElements.define('wlsn-header', WLSNHeader);
}
